import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';
/** Совпадает ли файл с одним элементом `accept`. Экспорт — для тестов. */
export function fileMatchesAccept(file, accept) {
    if (!accept || accept.trim() === '')
        return true;
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();
    return accept
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .some((rule) => {
        if (rule.startsWith('.'))
            return name.endsWith(rule);
        if (rule.endsWith('/*'))
            return type.startsWith(rule.slice(0, -1));
        return type === rule;
    });
}
function formatMiB(bytes) {
    const mib = bytes / (1024 * 1024);
    if (mib >= 1024)
        return `${(mib / 1024).toFixed(mib % 1024 === 0 ? 0 : 1).replace('.', ',')} ГиБ`;
    return `${Math.round(mib)} МиБ`;
}
/** Разделить файлы на принятые и отвергнутые. Экспорт — для тестов и вызывающих. */
export function partitionFiles(files, opts) {
    const accepted = [];
    const rejected = [];
    for (const file of files) {
        if (!fileMatchesAccept(file, opts.accept)) {
            rejected.push({
                file,
                reason: 'type',
                message: `«${file.name}»: этот тип файла не подходит`,
            });
        }
        else if (opts.maxBytes !== undefined && file.size > opts.maxBytes) {
            rejected.push({
                file,
                reason: 'size',
                message: `«${file.name}»: больше ${formatMiB(opts.maxBytes)}`,
            });
        }
        else {
            accepted.push(file);
        }
    }
    return { accepted, rejected };
}
/**
 * Зона перетаскивания файлов: drag-and-drop + кнопка «Выбрать файлы»
 * (скрытый `<input type="file">` внутри кита — модулям сырой `<input>`
 * запрещён ESLint'ом). Фильтр `accept` / `maxBytes` применяется к ОБОИМ путям:
 * у перетаскивания браузер `accept` не проверяет.
 *
 * Сама ничего не загружает — отдаёт `File[]` вызывающему.
 */
export const FileDropZone = React.forwardRef(({ onFiles, onReject, accept, multiple = false, maxBytes, disabled = false, title = 'Перетащите файлы сюда или выберите', hint, chooseLabel = 'Выбрать файлы', className, }, ref) => {
    const inputRef = React.useRef(null);
    const [over, setOver] = React.useState(false);
    // Счётчик входов: dragenter/dragleave приходят и от дочерних элементов.
    const depth = React.useRef(0);
    const handle = (list) => {
        if (!list || disabled)
            return;
        const all = Array.from(list);
        const files = multiple ? all : all.slice(0, 1);
        const { accepted, rejected } = partitionFiles(files, { accept, maxBytes });
        if (rejected.length > 0)
            onReject?.(rejected);
        if (accepted.length > 0)
            onFiles(accepted);
    };
    return (_jsxs("div", { ref: ref, "data-testid": "file-drop-zone", "data-state": over ? 'over' : 'idle', "aria-disabled": disabled || undefined, className: cn('flex flex-col items-center justify-center gap-2 rounded-[var(--radius-3)] border-2 border-dashed border-[var(--color-border-default)] bg-[var(--color-surface-sunken)] p-6 text-center transition-colors', over && 'border-[var(--color-border-focus)] bg-[var(--color-state-info-bg)]', disabled && 'opacity-50', className), onDragEnter: (e) => {
            e.preventDefault();
            if (disabled)
                return;
            depth.current += 1;
            setOver(true);
        }, onDragOver: (e) => {
            e.preventDefault();
            if (!disabled)
                e.dataTransfer.dropEffect = 'copy';
        }, onDragLeave: (e) => {
            e.preventDefault();
            depth.current = Math.max(0, depth.current - 1);
            if (depth.current === 0)
                setOver(false);
        }, onDrop: (e) => {
            e.preventDefault();
            depth.current = 0;
            setOver(false);
            handle(e.dataTransfer.files);
        }, children: [_jsx(Upload, { "aria-hidden": true, className: "h-8 w-8 text-[var(--color-text-muted)]" }), _jsx("div", { className: "text-sm font-medium text-[var(--color-text-primary)]", children: title }), hint != null ? _jsx("div", { className: "text-xs text-[var(--color-text-muted)]", children: hint }) : null, _jsx(Button, { type: "button", variant: "secondary", size: "sm", className: "mt-1", disabled: disabled, onClick: () => inputRef.current?.click(), children: chooseLabel }), _jsx("input", { ref: inputRef, type: "file", hidden: true, "data-testid": "file-drop-input", accept: accept, multiple: multiple, disabled: disabled, onChange: (e) => {
                    handle(e.target.files);
                    // Повторный выбор того же файла должен снова дать onChange.
                    e.target.value = '';
                } })] }));
});
FileDropZone.displayName = 'FileDropZone';
//# sourceMappingURL=file-drop.js.map